import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'

import { fetchBatteries } from '../actions/batteries'
import { fetchMachines } from '../actions/machines'
import { fetchCustomers } from '../actions/customers'

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts'

import '../styles/DashboardView.css'
import '../style.css'

const TOTAL_BATTERY = 6500
const MIN_BATTERY = 4400

function getBatteryBar(bateria) {
  if (bateria === null || bateria === undefined) {
    return { squaresCount: 0, batteryColor: 'grey', percentage: 0 }
  }
  const percentage = Math.max(
    0,
    Math.min(100, ((bateria - MIN_BATTERY) / (TOTAL_BATTERY - MIN_BATTERY)) * 100)
  )
  let squaresCount, batteryColor
  if (percentage >= 83.3) { squaresCount = 5; batteryColor = 'green' }
  else if (percentage >= 66.7) { squaresCount = 4; batteryColor = 'green' }
  else if (percentage >= 50) { squaresCount = 3; batteryColor = 'orange' }
  else if (percentage >= 33.3) { squaresCount = 2; batteryColor = 'orange' }
  else if (percentage >= 16.7) { squaresCount = 1; batteryColor = 'red' }
  else { squaresCount = 0; batteryColor = 'red' }
  return { squaresCount, batteryColor, percentage }
}

function getBatteryStatus(bateria, reported) {
  if (!reported || bateria === null || bateria === undefined) return 'Null'
  if (bateria < MIN_BATTERY) return 'FL'
  if (bateria < 5800) return 'RK'
  return 'AV'
}

const TYPE_HEIGHT = { S: 45, M: 90, L: 135, XL: 180, '2XL': 225, '3XL': 270 }

class BatteriesDashboardView extends React.Component {
  constructor(props) {
    super(props)

    const today = new Date().toISOString().substring(0, 10)
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)

    this.state = {
      selectedCustomer: '',
      selectedMachine: '',
      startDate: weekAgo,
      endDate: today,
      sliderIndex: 0,
      hasQueried: false,
      lockerModal: null,
    }

    this.handleFetch = this.handleFetch.bind(this)
  }

  componentDidMount() {
    this.props.fetchMachines()
    if (this.props.role === 'ADMIN') {
      this.props.fetchCustomers()
    }
  }

  handleFetch() {
    const { selectedCustomer, selectedMachine, startDate, endDate } = this.state
    if (!selectedCustomer && !selectedMachine) return
    this.props.fetchBatteries(
      selectedMachine || undefined,
      selectedCustomer || undefined,
      startDate || undefined,
      endDate || undefined
    )
    this.setState({ sliderIndex: 0, hasQueried: true })
  }

  renderLockerGrid(doc, machine, docs, serial) {
    const matrixRender = []
    let columnHeight = 0
    let matrixWidth = 0
    let checkKiosc = 0
    let matrixIndex = 0

    const lastIndex = machine ? (machine.LastIndex || doc.Lockers.length) : doc.Lockers.length
    const firstIndex = machine ? (machine.FirstIndex || 1) : 1

    doc.Lockers.forEach((lock, index) => {
      if (matrixIndex >= lastIndex) return

      const mode = lock.Mode || 'B'
      const wide = lock.Wide || 'N'
      const type = lock.Type || 'S'
      const lockerTypeId = `locker${mode}${wide}${type}`
      const lockerStatus = getBatteryStatus(lock.Bateria, lock.Reported)
      const voltageText = lock.Reported && lock.Bateria != null
        ? `${(lock.Bateria / 1000).toFixed(1)}V`
        : '?'

      columnHeight += (TYPE_HEIGHT[type] || 45)

      if (columnHeight >= 270) {
        columnHeight = 0
        matrixWidth += wide === 'W' ? 60 : 45
      }

      matrixRender.push(
        <div
          key={index}
          id={lockerTypeId}
          title={`${lock.BLEName || '—'} · ${voltageText}`}
          style={{ cursor: 'pointer' }}
          onClick={() => this.setState({ lockerModal: { BLEName: lock.BLEName, Matricula: lock.Matricula, serial, docs } })}
        >
          <p>{index + firstIndex}</p>
          <div id={lockerStatus}><p>{voltageText}</p></div>
        </div>
      )

      matrixIndex++

      if (lock.Position === 'C' && checkKiosc === 0) {
        columnHeight += 135
        matrixRender.push(<div key={`k${matrixIndex}`} id="lockerKioskLeft" />)
        checkKiosc = 1
      } else if (lock.Position === 'E' && checkKiosc === 0) {
        columnHeight += 90
        matrixRender.push(<div key={`k${matrixIndex}`} id="lockerEcomKioskLeft" />)
        checkKiosc = 1
      } else if (lock.Position === 'C' && checkKiosc === 1) {
        columnHeight += 135
        matrixRender.push(<div key={`k${matrixIndex}`} id="lockerKioskRight" />)
        checkKiosc = 0
      } else if (lock.Position === 'E' && checkKiosc === 1) {
        columnHeight += 90
        matrixRender.push(<div key={`k${matrixIndex}`} id="lockerEcomKioskRight" />)
        checkKiosc = 0
      }
    })

    return (
      <div id="matrixContainer" style={{ width: matrixWidth + 60, overflowX: 'auto' }}>
        <div className="row" id="lockerMatrix">
          {matrixRender}
        </div>
      </div>
    )
  }

  renderMachineTile(serial, docs, selectedDate, machines) {
    const doc = docs.find(d => d.ScanDate === selectedDate)
    if (!doc) return null

    const machineId = doc.Machine && (doc.Machine._id || doc.Machine)
    const machine = machines && machineId
      ? machines.find(m => String(m._id) === String(machineId))
      : null

    const total = doc.Lockers.length
    const reported = doc.Lockers.filter(l => l.Reported).length
    const unreported = total - reported
    const reportedWithBat = doc.Lockers.filter(l => l.Reported && l.Bateria != null)
    const avgBat = reportedWithBat.length > 0
      ? reportedWithBat.reduce((sum, l) => sum + l.Bateria, 0) / reportedWithBat.length
      : null

    const goodCount = doc.Lockers.filter(l => l.Reported && l.Bateria != null && l.Bateria >= 5800).length
    const midCount  = doc.Lockers.filter(l => l.Reported && l.Bateria != null && l.Bateria >= MIN_BATTERY && l.Bateria < 5800).length
    const lowCount  = doc.Lockers.filter(l => l.Reported && l.Bateria != null && l.Bateria < MIN_BATTERY).length

    const avgBar = getBatteryBar(avgBat)
    const avgColor = avgBat == null ? '#6b7280'
      : avgBat < MIN_BATTERY ? '#dc3545'
      : avgBat < 5800        ? '#fc9700'
      :                        '#28a745'

    return (
      <div key={serial} className="dashboard-card-section mb-3">
        <div className="card-header-custom" style={{
          background: 'linear-gradient(135deg, #022040, #0a4080)',
          color: '#fff',
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontWeight: 700,
          fontSize: 16,
        }}>
          <i className="fas fa-server" />
          {serial}
          <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 13, opacity: 0.8 }}>
            — {doc.ScanDate}
          </span>
        </div>

        <div className="card-body-custom">
          <div className="dashboard-kpi-row mb-3">

            <div className="dashboard-kpi-card total">
              <div className="kpi-icon"><i className="fas fa-list" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Total taquillas</div>
                <div className="kpi-value">{total}</div>
              </div>
            </div>

            <div className="dashboard-kpi-card green-block">
              <div className="kpi-icon"><i className="fas fa-check-circle" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Reportadas</div>
                <div className="kpi-value">{reported}</div>
                <div className="small text-muted">{total > 0 ? ((reported / total) * 100).toFixed(0) : 0}%</div>
              </div>
            </div>

            <div className="dashboard-kpi-card pinpad-block">
              <div className="kpi-icon"><i className="fas fa-question-circle" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Sin reporte</div>
                <div className="kpi-value">{unreported}</div>
              </div>
            </div>

            <div className="dashboard-kpi-card income-day" style={{ flex: '2 1 200px' }}>
              <div className="kpi-icon"><i className="fas fa-battery-half" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Batería media</div>
                {avgBat != null ? (
                  <>
                    <div className="kpi-value" style={{ color: avgColor }}>
                      {(avgBat / 1000).toFixed(2)} V
                    </div>
                    <div className="battery-indicator mt-1" style={{ justifyContent: 'flex-start' }}>
                      <div className="battery-shape">
                        {Array.from({ length: avgBar.squaresCount }, (_, i) => (
                          <div key={i} className={`battery-square ${avgBar.batteryColor}`} />
                        ))}
                      </div>
                      <div className="battery-info">
                        <div className="battery-percentage">{avgBar.percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="kpi-value" style={{ color: '#6b7280', fontSize: 16 }}>N/D</div>
                )}
              </div>
            </div>

            <div className="dashboard-kpi-card door-block">
              <div className="kpi-icon"><i className="fas fa-battery-full" /></div>
              <div className="kpi-content">
                <div className="kpi-label">OK ≥5.8V</div>
                <div className="kpi-value" style={{ color: '#28a745' }}>{goodCount}</div>
              </div>
            </div>

            <div className="dashboard-kpi-card broken">
              <div className="kpi-icon"><i className="fas fa-battery-quarter" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Medio 4.4–5.8V</div>
                <div className="kpi-value" style={{ color: '#fc9700' }}>{midCount}</div>
              </div>
            </div>

            <div className="dashboard-kpi-card red-block">
              <div className="kpi-icon"><i className="fas fa-battery-empty" /></div>
              <div className="kpi-content">
                <div className="kpi-label">Bajo &lt;4.4V</div>
                <div className="kpi-value" style={{ color: '#dc3545' }}>{lowCount}</div>
              </div>
            </div>

          </div>

          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span><span style={legendDot('#35f734')} /> ≥5.8V (OK)</span>
            <span><span style={legendDot('#fc9700')} /> 4.4–5.8V (medio)</span>
            <span><span style={legendDot('#dc3545')} /> &lt;4.4V (bajo)</span>
            <span><span style={legendDot('#0e0e0e')} /> Sin reporte</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {this.renderLockerGrid(doc, machine, docs, serial)}
          </div>
        </div>
      </div>
    )
  }

  renderLockerModal() {
    const { lockerModal } = this.state
    if (!lockerModal) return null

    const { BLEName, Matricula, serial, docs } = lockerModal

    const chartData = docs
      .slice()
      .sort((a, b) => a.ScanDate.localeCompare(b.ScanDate))
      .map(d => {
        const lock = d.Lockers.find(l => l.Matricula === Matricula)
        return {
          date: d.ScanDate.slice(5),
          fullDate: d.ScanDate,
          bateria: (lock && lock.Reported && lock.Bateria != null)
            ? parseFloat((lock.Bateria / 1000).toFixed(3))
            : null,
        }
      })

    const hasData = chartData.some(d => d.bateria != null)
    const lastEntry = [...chartData].reverse().find(d => d.bateria != null)
    const lastVoltage = lastEntry ? lastEntry.bateria : null
    const voltColor = lastVoltage == null ? '#6b7280'
      : lastVoltage < 4.4 ? '#dc3545'
      : lastVoltage < 5.8 ? '#fc9700'
      :                      '#28a745'

    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(2,32,64,0.72)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
        onClick={() => this.setState({ lockerModal: null })}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
            width: '100%',
            maxWidth: 720,
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal header */}
          <div style={{
            background: 'linear-gradient(135deg, #022040, #0a4080)',
            color: '#fff',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fas fa-battery-half" style={{ fontSize: 18 }} />
              <span style={{ fontWeight: 700, fontSize: 16 }}>Taquilla {Matricula}</span>
              <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>
                {BLEName || '—'} — {serial}
              </span>
            </div>
            <button
              onClick={() => this.setState({ lockerModal: null })}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            >
              <i className="fas fa-times" />
            </button>
          </div>

          {/* Modal body */}
          <div style={{ padding: '20px 20px 16px' }}>
            {/* Last known voltage badge */}
            {lastVoltage != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Último valor:</span>
                <span style={{ fontWeight: 700, fontSize: 22, color: voltColor }}>
                  {lastVoltage.toFixed(3)} V
                </span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>({lastEntry.fullDate})</span>
              </div>
            )}

            {!hasData ? (
              <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                <i className="fas fa-chart-line" style={{ fontSize: 32, marginBottom: 10, display: 'block', color: '#cbd5e1' }} />
                Sin datos de batería para esta taquilla en el período seleccionado.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 24, height: 3, background: '#0b97ff', borderRadius: 2 }} />Batería (V)</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 24, borderTop: '2px dashed #dc3545' }} />Mínimo 4.4V</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 24, borderTop: '2px dashed #fc9700' }} />Umbral 5.8V</span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={[4.0, 6.8]}
                      tickFormatter={v => `${v.toFixed(1)}V`}
                      tick={{ fontSize: 11 }}
                      width={50}
                    />
                    <Tooltip
                      formatter={value => value != null ? [`${value.toFixed(3)} V`, 'Batería'] : ['Sin datos', 'Batería']}
                      labelFormatter={label => `Fecha: ${label}`}
                    />
                    <ReferenceLine y={4.4} stroke="#dc3545" strokeDasharray="4 3" label={{ value: '4.4V', position: 'insideTopRight', fontSize: 10, fill: '#dc3545' }} />
                    <ReferenceLine y={5.8} stroke="#fc9700" strokeDasharray="4 3" label={{ value: '5.8V', position: 'insideTopRight', fontSize: 10, fill: '#fc9700' }} />
                    <Line
                      type="monotone"
                      dataKey="bateria"
                      stroke="#0b97ff"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: '#0b97ff', strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: '#022040' }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { batteries, loading, error, machines, customers, role } = this.props
    const {
      selectedCustomer, selectedMachine,
      startDate, endDate, sliderIndex, hasQueried,
    } = this.state

    // Machines filtered by selected customer for the dropdown
    const filteredMachines = machines
      ? machines.filter(m => {
          if (!selectedCustomer) return true
          const cid = m.Customer && (m.Customer._id || m.Customer)
          return String(cid) === String(selectedCustomer)
        })
      : []

    // Group fetched battery docs by machine serial
    const byMachine = {}
    if (Array.isArray(batteries)) {
      batteries.forEach(doc => {
        if (!byMachine[doc.Serial]) byMachine[doc.Serial] = []
        byMachine[doc.Serial].push(doc)
      })
    }

    // Sorted unique scan dates across all returned docs
    const allDates = Array.isArray(batteries)
      ? [...new Set(batteries.map(d => d.ScanDate))].sort()
      : []

    const selectedDate = allDates[sliderIndex] || null

    return (
      <div className="dashboard-view">

        {/* ── Header + filters ──────────────────────────────────────── */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-battery-half" style={{ marginRight: 8 }} />
              Historial de Baterías
            </h1>
            {hasQueried && allDates.length > 0 && (
              <div className="small text-muted mt-1">
                {allDates.length} día(s) con datos · {Object.keys(byMachine).length} máquina(s)
              </div>
            )}
          </div>

          <div className="dashboard-actions">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>

              {role === 'ADMIN' && (
                <select
                  className="dashboard-filter-input"
                  value={selectedCustomer}
                  onChange={e => this.setState({ selectedCustomer: e.target.value, selectedMachine: '' })}
                  style={{ minWidth: 200 }}
                >
                  <option value="">Cliente (todos)</option>
                  {Array.isArray(customers) && customers
                    .filter(c => c.Type === 'GENERAL' || c.Type === 'LICENCIA_BASICA' || c.Type === 'LICENCIA_PRO')
                    .map(c => (
                      <option key={c._id} value={c._id}>{c.Fullname}</option>
                    ))
                  }
                </select>
              )}

              <select
                className="dashboard-filter-input"
                value={selectedMachine}
                onChange={e => this.setState({ selectedMachine: e.target.value })}
                style={{ minWidth: 180 }}
              >
                <option value="">Máquina (todas)</option>
                {filteredMachines.map(m => (
                  <option key={m._id} value={m._id}>{m.Serial}</option>
                ))}
              </select>

              <input
                type="date"
                className="dashboard-filter-input"
                value={startDate}
                onChange={e => this.setState({ startDate: e.target.value })}
              />
              <input
                type="date"
                className="dashboard-filter-input"
                value={endDate}
                onChange={e => this.setState({ endDate: e.target.value })}
              />

              <button
                className="btn btn-vw"
                onClick={this.handleFetch}
                disabled={loading || (!selectedCustomer && !selectedMachine)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8 }}
              >
                <i className="fas fa-search" />
                {loading ? 'Cargando...' : 'Consultar'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Error / loading ─────────────────────────────────────────── */}
        {error && <ErrorMessage message={error} />}
        {loading && <LoadingProgress />}

        {/* ── Selection required notice ───────────────────────────────── */}
        {!selectedCustomer && !selectedMachine && !loading && (
          <div className="dashboard-card-section p-4" style={{ display: 'flex', alignItems: 'center', gap: 14, color: '#374151' }}>
            <i className="fas fa-info-circle" style={{ fontSize: 28, color: '#0b97ff', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Selecciona un cliente o una máquina</div>
              <div className="small text-muted">Elige al menos un cliente o una máquina en los filtros de arriba y pulsa <strong>Consultar</strong> para ver el historial de baterías.</div>
            </div>
          </div>
        )}

        {/* ── Results ─────────────────────────────────────────────────── */}
        {hasQueried && !loading && (selectedCustomer || selectedMachine) && (
          allDates.length === 0 ? (
            <div className="dashboard-card-section p-4 text-center text-muted">
              <i className="fas fa-battery-slash" style={{ fontSize: 32, marginBottom: 12, display: 'block', color: '#cbd5e1' }} />
              No hay registros de baterías para el período y filtros seleccionados.
            </div>
          ) : (
            <>
              {/* ── Day slider ─────────────────────────────────────────── */}
              <div className="dashboard-card-section mb-3">
                <div className="card-header-custom" style={{
                  background: 'linear-gradient(135deg, #022040, #0a4080)',
                  color: '#fff',
                  padding: '12px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 700,
                }}>
                  <i className="fas fa-calendar-alt" />
                  <span style={{ marginLeft: 6 }}>Evolución temporal</span>
                  <span style={{ fontWeight: 400, fontSize: 13, opacity: 0.8, marginLeft: 8 }}>
                    {allDates.length} día(s) con datos
                  </span>
                </div>
                <div className="card-body-custom">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
                    <span>{allDates[0]}</span>
                    <span style={{ fontWeight: 700, color: '#022040', fontSize: 15 }}>
                      <i className="fas fa-calendar-day" style={{ marginRight: 6, color: '#0b97ff' }} />
                      {selectedDate}
                    </span>
                    <span>{allDates[allDates.length - 1]}</span>
                  </div>

                  <input
                    type="range"
                    className="form-control-range"
                    min={0}
                    max={allDates.length - 1}
                    value={sliderIndex}
                    onChange={e => this.setState({ sliderIndex: parseInt(e.target.value, 10) })}
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#0b97ff' }}
                  />

                  {allDates.length <= 31 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, flexWrap: 'wrap', gap: 2 }}>
                      {allDates.map((d, i) => (
                        <span
                          key={d}
                          onClick={() => this.setState({ sliderIndex: i })}
                          style={{
                            cursor: 'pointer',
                            fontSize: 10,
                            color: i === sliderIndex ? '#0b97ff' : '#cbd5e1',
                            fontWeight: i === sliderIndex ? 700 : 400,
                            userSelect: 'none',
                            padding: '2px 0',
                            borderBottom: i === sliderIndex ? '2px solid #0b97ff' : '2px solid transparent',
                            transition: 'color 0.15s',
                          }}
                        >
                          {d.slice(5)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Machine tiles for selected date ── */}
              {Object.entries(byMachine).map(([serial, docs]) =>
                this.renderMachineTile(serial, docs, selectedDate, machines)
              )}
            </>
          )
        )}

        {this.renderLockerModal()}
      </div>
    )
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function legendDot(color) {
  return {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: color,
    marginRight: 4,
    verticalAlign: 'middle',
  }
}

// ─── Redux wiring ─────────────────────────────────────────────────────────────
const mapStateToProps = state => ({
  batteries: state.batteries.batteries,
  loading: state.batteries.loading,
  error: state.batteries.error,
  machines: state.machines.machines,
  customers: state.customers.customers,
  role: state.auth.role,
})

const mapDispatchToProps = dispatch => ({
  fetchBatteries: bindActionCreators(fetchBatteries, dispatch),
  fetchMachines: bindActionCreators(fetchMachines, dispatch),
  fetchCustomers: bindActionCreators(fetchCustomers, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(BatteriesDashboardView)
